import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Breadcrumbs,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import AddIcon from "@material-ui/icons/Add";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import HomeIcon from "@material-ui/icons/Home";
import { useHistory, useLocation } from "react-router-dom";
import { CustomTheme } from "../../../../../../types/customTheme";
import WrapperNode from "./WrapperNode";

interface DirectoryContentViewProps {
  currentDirectory: any;
  currentCategory?: any;
  directoryStack: any[];
  menuNodeList: any;
  selectedMenuId: string;
  selectedMenuName: string;
  locationId: string;
  setMenuNodeList: any;
  productVersion: any;
  tags: any;
  onNavigateToDirectory: (directory: any, stack: any[]) => void;
  onBack: () => void;
  baseUrl: string;
  onAddItemClick?: () => void;
  handleOpenDirectoryEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenProductEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenMappingModal?: (
    event: any,
    category: any,
    item: any,
    type: string,
    isMap?: any,
  ) => void;
  handleOpenProductOrDirectoryModals?: (category: any, wrapper?: any) => void;
  setIsProductAdd?: any;
  setError?: any;
  isOpenWrapperEditModal?: any;
  isRoot?: any;
  firstWrapperId?: any;
  wrapperId?: any;
  removedLinkedIdLists?: any;
  handleClickLinkedId?: any;
  handleClearStrip?: any;
  handleNavigate?: any;
  onDirectoryUpdated: any;
  isEditDisabled: any;
}

const DirectoryContentView: React.FC<DirectoryContentViewProps> = ({
  currentDirectory,
  currentCategory,
  directoryStack,
  menuNodeList,
  selectedMenuId,
  selectedMenuName,
  locationId,
  setMenuNodeList,
  productVersion,
  tags,
  onNavigateToDirectory,
  onBack,
  baseUrl,
  onAddItemClick,
  handleOpenDirectoryEditModal,
  handleOpenProductEditModal,
  handleOpenMappingModal,
  handleOpenProductOrDirectoryModals,
  setIsProductAdd,
  setError,
  isOpenWrapperEditModal,
  isRoot,
  firstWrapperId,
  wrapperId,
  removedLinkedIdLists,
  handleClickLinkedId,
  handleClearStrip,
  handleNavigate,
  onDirectoryUpdated,isEditDisabled
}) => {
  const theme: CustomTheme = useTheme();
  const isMobile = useMediaQuery("(max-width: 960px)");
  const history = useHistory();
  // Fix: Persist view mode in localStorage
  const [isGridView, setIsGridView] = useState(() => {
    const saved = localStorage.getItem("directoryViewMode");
    return saved !== null ? saved === "grid" : true;
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHidden, setShowHidden] = useState(false);

  const currentContent = currentDirectory?.wrapper || {};

  // Fix: Save view mode preference
  const handleGridViewToggle = (gridView: boolean) => {
    setIsGridView(gridView);
    localStorage.setItem("directoryViewMode", gridView ? "grid" : "list");
  };

  // Inside the component, add:
  const location = useLocation();

  const handleItemSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (isChecked: boolean, wrapper: any) => {
    let idsInWrapper: string[] = [];
    if (wrapper) {
      Object.values(wrapper).forEach((item: any) => {
        if (item?.id && !item?.directory) {
          idsInWrapper.push(item.id.toString());
        }
      });
    }
    if (isChecked) {
      setSelectedIds((prev) => [...new Set([...prev, ...idsInWrapper])]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => !idsInWrapper.includes(id)));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      history.push(`${baseUrl}/${selectedMenuId}`);
      onNavigateToDirectory(null, []);
    } else if (index === 0 && currentCategory) {
      history.push(`${baseUrl}/${selectedMenuId}`);
      onNavigateToDirectory(null, []);
    } else {
      const adjustedIndex = currentCategory ? index - 1 : index;
      if (adjustedIndex >= 0 && adjustedIndex < directoryStack.length) {
        const targetStack = directoryStack.slice(0, adjustedIndex + 1);
        const targetDir = targetStack[targetStack.length - 1];
        onNavigateToDirectory(targetDir, targetStack);

        let path = `${baseUrl}/${selectedMenuId}`;
        for (let i = 0; i < targetStack.length; i++) {
          path += `/directory/${targetStack[i].id}`;
        }
        history.replace(path);
      } else if (adjustedIndex === -1 && currentCategory) {
        history.push(`${baseUrl}/${selectedMenuId}`);
        onNavigateToDirectory(null, []);
      }
    }
  };

  const handleDirectoryNavigate = (
    directory: any,
    category: any,
    menuItem: any,
  ) => {
    const newStack = [...directoryStack];
    newStack.push({
      type: "directory",
      id: directory.id,
      name: directory.name,
      category: category,
      directoryData: directory,
    });

    onNavigateToDirectory(directory, newStack);

    let path = `${baseUrl}/${selectedMenuId}`;
    for (let i = 0; i < newStack.length; i++) {
      path += `/directory/${newStack[i].id}`;
    }
    history.replace(path);
  };

  // Handle product navigation
  const handleProductNavigate = (
    product: any,
    wrapper: any,
    category: any,
    menuItem: any,
  ) => {
    // Build URL with full directory path
    let path = `${baseUrl}/${selectedMenuId}`;

    // Add all directory IDs from the stack
    for (let i = 0; i < directoryStack.length; i++) {
      path += `/directory/${directoryStack[i].id}`;
    }

    // Add product ID
    path += `/product/${product.id}`;

    history.push(path);
  };

  const renderBreadcrumbs = () => {
    const breadcrumbItems = [];

    breadcrumbItems.push(
      <Button
        key="menu"
        size="small"
        onClick={() => handleBreadcrumbClick(-1)}
        style={{
          color: theme.palette.custom.orange.contrastText,
          textTransform: "none",
          padding: "4px 8px",
          minWidth: "auto",
        }}
      >
        <HomeIcon style={{ fontSize: "16px", marginRight: "4px" }} />
        {selectedMenuName}
      </Button>,
    );

    if (currentCategory) {
      breadcrumbItems.push(
        <Button
          key="category"
          size="small"
          onClick={() => handleBreadcrumbClick(0)}
          style={{
            color:
              directoryStack.length === 0
                ? theme.palette.custom.yellow.main
                : theme.palette.custom.orange.contrastText,
            textTransform: "none",
            padding: "4px 8px",
            minWidth: "auto",
          }}
        >
          {currentCategory.name}
        </Button>,
      );
    }

    directoryStack.forEach((item, index) => {
      const isLast = index === directoryStack.length - 1;
      breadcrumbItems.push(
        <Button
          key={item.id}
          size="small"
          onClick={() => handleBreadcrumbClick(index + 1)}
          style={{
            color: isLast
              ? theme.palette.custom.yellow.main
              : theme.palette.custom.orange.contrastText,
            textTransform: "none",
            padding: "4px 8px",
            minWidth: "auto",
          }}
        >
          {item.name}
        </Button>,
      );
    });

    return (
      <Box
        style={{
          padding: "12px 24px",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              style={{ color: theme.palette.custom.orange.contrastText }}
            />
          }
        >
          {breadcrumbItems}
        </Breadcrumbs>
      </Box>
    );
  };

  const contentItems = Object.values(currentContent);

  const filteredItems = contentItems.filter((item: any) => {
    if (!item) return false;
    // if (!showHidden && !item.valid) return false;
    if (
      searchTerm &&
      !item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  const addNodeButtonStyle: any = {
    width: "100%",
    marginTop: "14px",
    display: "flex",
    borderRadius: "10px",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    backgroundColor: theme.palette.background.entity_background,
    border: `1px dashed ${theme.palette.custom.yellow.main}`,
    height: isMobile ? "100px" : "auto",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  };

  return (
    <div key={`directory_content_${currentDirectory?.id}_${forceUpdateKey}`}>
      <Box
        style={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: "12px" }}
          flexWrap="wrap"
        >
          <Button
            onClick={onBack}
            startIcon={<Icon icon="lucide:arrow-left" width="18" />}
          >
            Back to Menus
          </Button>
          <div
            style={{
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              width: "200px",
              height: "40px",
              border: "1px solid #374151",
              backgroundColor: theme.palette.background.entity_background,
            }}
          >
            <Icon
              icon="lucide:search"
              width="18"
              color={theme.palette.custom.orange.contrastText}
            />
            <input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: "none",
                backgroundColor: "transparent",
                marginLeft: "8px",
                outline: "none",
                width: "100%",
                color: theme.palette.custom.orange.contrastText,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "12px",
            }}
          >
            <Box
              onClick={() => setShowHidden(!showHidden)}
              style={{
                width: "34px",
                height: "18px",
                backgroundColor: showHidden ? "#8b5cf6" : "#374151",
                borderRadius: "10px",
                position: "relative",
                cursor: "pointer",
                marginRight: "8px",
              }}
            >
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "2px",
                  left: showHidden ? "18px" : "2px",
                  transition: "0.2s",
                }}
              />
            </Box>
            <Typography
              style={{ color: theme.palette.secondary.main, fontSize: "14px" }}
            >
              Show Hidden
            </Typography>
          </div>
        </Box>
        <Box display="flex" style={{ gap: "12px" }} alignItems="center">
          <Box display="flex" style={{ gap: "8px" }}>
            <IconButton
              onClick={() => handleGridViewToggle(false)}
              style={{
                backgroundColor: !isGridView
                  ? theme.palette.custom.yellow.main
                  : theme.palette.background.entity_background,
                borderRadius: "8px",
                padding: "8px",
                minWidth: "40px",
                height: "40px",
                color: theme.palette.custom.orange.contrastText,
              }}
            >
              <Icon icon="mdi:format-list-bulleted" width={20} height={20} />
            </IconButton>
            <IconButton
              onClick={() => handleGridViewToggle(true)}
              style={{
                backgroundColor: isGridView
                  ? theme.palette.custom.yellow.main
                  : theme.palette.background.entity_background,
                borderRadius: "8px",
                padding: "8px",
                minWidth: "40px",
                height: "40px",
                color: theme.palette.custom.orange.contrastText,
              }}
            >
              <Icon icon="mdi:view-grid-outline" width={20} height={20} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {renderBreadcrumbs()}

      <Box style={{ padding: "0 24px 24px 24px" }}>
        <Grid container spacing={isGridView ? 2 : 0}>
          {filteredItems.map((wrapperItem: any, idx: any) => (
            <WrapperNode
              key={`${wrapperItem?.id}_${currentDirectory?.id}_${forceUpdateKey}_${idx}`}
              wrapper={wrapperItem}
              category={currentDirectory}
              isGridView={isGridView}
              selectedIds={selectedIds}
              handleItemSelect={handleItemSelect}
              tags={tags}
              menuNodeList={menuNodeList}
              setMenuNodeList={setMenuNodeList}
              menuId={selectedMenuId}
              categoryId={currentDirectory?.id}
              productVersion={productVersion}
              locationId={locationId}
              onDirectoryVisibilityUpdated={() =>
                setForceUpdateKey((prev) => prev + 1)
              }
              forceUpdateKey={forceUpdateKey}
              handleSelectAll={handleSelectAll}
              handleOpenDirectoryEditModal={handleOpenDirectoryEditModal}
              handleOpenProductEditModal={handleOpenProductEditModal}
              handleOpenMappingModal={handleOpenMappingModal}
              handleOpenProductOrDirectoryModals={
                handleOpenProductOrDirectoryModals
              }
              setIsProductAdd={setIsProductAdd}
              setError={setError}
              onDirectoryNavigate={handleDirectoryNavigate}
              onProductNavigate={handleProductNavigate}
              currentCategory={currentDirectory}
              currentMenuItem={menuNodeList?.[selectedMenuId]}
              isOpenWrapperEditModal={isOpenWrapperEditModal}
              isRoot={isRoot}
              firstWrapperId={firstWrapperId}
              wrapperId={wrapperId}
              removedLinkedIdLists={removedLinkedIdLists}
              handleClickLinkedId={handleClickLinkedId}
              handleClearStrip={handleClearStrip}
              handleNavigate={handleNavigate}
              onDirectoryUpdated={onDirectoryUpdated}
              isEditDisabled={isEditDisabled}
            />
          ))}

          <Grid item md={6} xs={12} lg={4}>
            <Button
              variant="contained"
              style={addNodeButtonStyle}
              onClick={onAddItemClick}
              disabled={isEditDisabled}
            >
              <Typography>
                <AddIcon fontSize="large" />
              </Typography>
              <Typography style={{ marginLeft: "12px" }}>
                Add New Item
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default DirectoryContentView;
